/**
 * ErrorSnackbar
 * Global MUI Snackbar that displays errors from the ErrorContext.
 * Auto-dismisses after 6 seconds. Uses different colors per error type.
 */
import React from 'react';
import { Snackbar, Alert, AlertColor, Typography, Box, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useError, ErrorSeverity, ErrorType } from '../../context/ErrorContext';

// ---- Helpers ----

const GOLD = '#C9A96E';

/** Map ErrorSeverity to MUI AlertColor. */
function toAlertColor(severity: ErrorSeverity): AlertColor {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'error';
  }
}

/** Human-readable label for the error type. */
function errorTypeLabel(type: ErrorType): string {
  switch (type) {
    case 'network':
      return 'Network';
    case 'auth':
      return 'Authentication';
    case 'forbidden':
      return 'Access Denied';
    case 'validation':
      return 'Validation';
    case 'server':
      return 'Server Error';
    default:
      return 'Error';
  }
}

// ---- Component ----

const ErrorSnackbar: React.FC = () => {
  const { currentError, dismissError } = useError();

  if (!currentError) {
    return null;
  }

  const alertColor = toAlertColor(currentError.severity);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    // Don't dismiss on clickaway
    if (reason === 'clickaway') return;
    dismissError(currentError.id);
  };

  return (
    <Snackbar
      open={!!currentError}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ maxWidth: 480 }}
    >
      <Alert
        severity={alertColor}
        variant="filled"
        onClose={handleClose}
        action={
          <IconButton size="small" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          width: '100%',
          alignItems: 'center',
          // Luxury styling overrides
          ...(currentError.severity === 'error' && {
            backgroundColor: '#c62828',
          }),
          ...(currentError.severity === 'warning' && {
            backgroundColor: '#e65100',
          }),
          ...(currentError.severity === 'info' && {
            backgroundColor: '#1a1a1a',
            border: `1px solid ${GOLD}`,
          }),
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {errorTypeLabel(currentError.type)}
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.4, opacity: 0.95 }}>
            {currentError.message}
          </Typography>
          {currentError.details && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, opacity: 0.75, fontStyle: 'italic' }}
            >
              {currentError.details}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default ErrorSnackbar;
